import {
  useCallback,
  createContext,
  useEffect,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type PropsWithChildren,
} from 'react';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Modal } from '../../components/ui/Modal';
import introductionVideo from '../../assets/videos/Introduction_Android.mp4';
import homePageVideo from '../../assets/videos/homePage_Android.mp4';
import {
  tutorialForAudience,
  type MeetWlbTutorialKey,
} from './tutorialAudience';
import './support.css';

interface MeetWeightLossBuddyContextValue {
  openHomePage: () => void;
  openIntroduction: () => void;
  openTutorials: () => void;
}

const MeetWeightLossBuddyContext =
  createContext<MeetWeightLossBuddyContextValue | null>(null);

const VIDEO_SOURCES: Record<MeetWlbTutorialKey, string> = {
  introduction: introductionVideo,
  homePage: homePageVideo,
};

export function MeetWeightLossBuddyProvider({ children }: PropsWithChildren) {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [activeTutorial, setActiveTutorial] =
    useState<MeetWlbTutorialKey | null>(null);
  const [feedCenter, setFeedCenter] = useState('50%');

  const alignWithFeed = useCallback((): void => {
    const feed = document.querySelector<HTMLElement>('.shell__content');
    if (!feed || window.innerWidth <= 768) {
      setFeedCenter('50%');
      return;
    }
    const bounds = feed.getBoundingClientRect();
    setFeedCenter(`${bounds.left + bounds.width / 2}px`);
  }, []);

  const value = useMemo(
    () => ({
      openHomePage: () => {
        alignWithFeed();
        setActiveTutorial('homePage');
      },
      openIntroduction: () => {
        alignWithFeed();
        setActiveTutorial('introduction');
      },
      openTutorials: () => {
        alignWithFeed();
        setActiveTutorial(tutorialForAudience(isAuthenticated));
      },
    }),
    [alignWithFeed, isAuthenticated],
  );

  useEffect(() => {
    if (!activeTutorial) return;
    alignWithFeed();
    window.addEventListener('resize', alignWithFeed);
    return () => window.removeEventListener('resize', alignWithFeed);
  }, [activeTutorial, alignWithFeed]);

  const backdropStyle = {
    '--meet-wlb-feed-center': feedCenter,
  } as CSSProperties;

  return (
    <MeetWeightLossBuddyContext.Provider value={value}>
      {children}

      {activeTutorial && (
        <Modal
          title={t(`sidebar.meetWlbModal.${activeTutorial}`)}
          labelledBy="meet-wlb-video-title"
          className="meet-wlb-video"
          backdropClassName="meet-wlb-backdrop"
          backdropStyle={backdropStyle}
          onClose={() => setActiveTutorial(null)}
        >
          <div className="meet-wlb-video__body">
            <video
              className="meet-wlb-video__player"
              src={VIDEO_SOURCES[activeTutorial]}
              controls
              autoPlay
              playsInline
              preload="metadata"
              aria-label={t(`sidebar.meetWlbModal.${activeTutorial}`)}
            />
          </div>
        </Modal>
      )}
    </MeetWeightLossBuddyContext.Provider>
  );
}

export const useMeetWeightLossBuddy = (): MeetWeightLossBuddyContextValue => {
  const context = useContext(MeetWeightLossBuddyContext);
  if (!context) {
    throw new Error('useMeetWeightLossBuddy must be used within its provider');
  }
  return context;
};
